import React, { useEffect, useState } from 'react';
import Datetime from 'react-datetime';
import {
  Checkbox,
  Col,
  Form,
  Input,
  Layout,
  message,
  Modal,
  Row,
  Skeleton,
  Typography,
} from 'antd';
import { get } from 'lodash';

import slugify from 'slugify';

import { PageSpinner } from 'components/page-spinner';
import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';

import FlashsaleApi from 'services/flashsales';
import WarehouseApi from 'services/warehouse';
import { errHandling } from 'utils';

import { EditableTable } from './parts';

import 'react-quill-new/dist/quill.snow.css';
import './style.scss';
import { useLocation, useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Search } = Input;
const { Title } = Typography;

const FIELDS = {
  BANNER_IMAGE: 'banner_image',
  CATEGORIES: 'categories',
  DESKTOP_MEDIA_URL: 'image',
  END_DATETIME: 'end_time',
  FLASHSALE: 'flashsale',
  MOBILE_MEDIA_URL: 'mobile_image',
  PRODUCT_LIST: 'collection_items_attributes',
  SLUG: 'slug',
  START_DATETIME: 'start_time',
  TITLE: 'title',
};

export function FlashsaleCreateEdit() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [allProducts, setAllProducts] = useState([]);
  const [flashsaleSlug, setFlashsaleSlug] = useState('');
  const [formLayout] = useState('vertical');
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadModal, setLoadModal] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [productList, setProductList] = useState([]);
  const [productNextPage, setProductNextPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [value, setValue] = useState({});
  const [warehouseList, setWarehouseList] = useState([]);
  const [mapListDeletedProduct, setMapListDeletedProduct] = useState({});
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;

  const buttonItemLayout =
    formLayout === 'horizontal'
      ? {
          wrapperCol: { offset: 4, span: 14 },
        }
      : null;

  async function getFlashsale() {
    try {
      setLoadingPage(true);
      if (id) {
        const { data } = await FlashsaleApi.getFlashsale(id);
        const FlashsaleDetail = data.data;
        setFlashsaleSlug(get(FlashsaleDetail, FIELDS.SLUG, ''));
        const products = get(FlashsaleDetail, 'products', []);
        setProductList(products);
        const existingProduct = products.map((product, index) => {
          const { id: product_id, flash_sale_stocks } = product;

          const currentFlashsaleStocks = flash_sale_stocks.filter(
            ({ product_collection_id }) => +product_collection_id === +id
          );

          return {
            id: currentFlashsaleStocks[0].collection_item_id,
            product_id,
            order_level: index,
            flash_sale_discounts_attributes: [],
            flash_sale_stocks_attributes: [],
          };
        });

        const initialMapDeletedProduct = {};
        existingProduct.forEach((item) => {
          initialMapDeletedProduct[item.product_id] = item;
        });

        setMapListDeletedProduct(initialMapDeletedProduct);
        setValue(FlashsaleDetail);
      }
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingPage(false);
    }
  }

  async function fetchAllProduct(
    page = 1,
    q = '',
    loadmore,
    total = 4,
    sort_by = 'created_at desc'
  ) {
    try {
      const { data } = await FlashsaleApi.getProductList(
        page,
        q,
        sort_by,
        total
      );
      const productData = data.data;
      const metadata = data.metadata;

      const newProduct = productData.map((product) => {
        if (productList.some((e) => e.id === product.id)) {
          product.checked = true;
        } else {
          product.checked = false;
        }
        return product;
      });

      setProductNextPage(metadata.next_cursor);
      if (loadmore) {
        setAllProducts((prevstate) => [...prevstate, ...newProduct]);
      } else {
        setAllProducts(newProduct);
      }
    } catch (err) {
      errHandling(err);
    }
  }

  async function showModal() {
    setLoadModal(true);
    setModalVisible(true);

    await fetchAllProduct(productNextPage);
    setLoadModal(false);
  }

  function hasErrors() {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  }

  async function handleSubmit(values) {
    // Note: avoid direct state mutation pattern!

    const valueCopy = { ...value };
    try {
      if (flashsaleSlug !== '') {
        valueCopy[FIELDS.SLUG] = flashsaleSlug;
      } else {
        valueCopy[FIELDS.SLUG] = null;
      }

      const isEditting = Boolean(id);

      values[FIELDS.PRODUCT_LIST] = productList.map((product, index) => {
        const {
          id: product_id,
          flash_sale_discounts,
          flash_sale_stocks,
        } = product;

        const currentFlashsaleDiscount =
          flash_sale_discounts.find(
            ({ product_collection_id }) => +product_collection_id === +id
          ) || {};
        const currentFlashsaleStocks = flash_sale_stocks.filter(
          ({ product_collection_id }) => +product_collection_id === +id
        );

        const flash_sale_discounts_attributes = [
          {
            fixed_price: currentFlashsaleDiscount.fixed_price,
            deduction_percentage: currentFlashsaleDiscount.deduction_percentage,
            ...(isEditting && { id: currentFlashsaleDiscount.id }),
          },
        ];

        const flash_sale_stocks_attributes = currentFlashsaleStocks
          .filter((e) => e.quantity !== undefined)
          .map((e) => {
            return {
              warehouse_id: e.warehouse_id,
              quantity: e.quantity === undefined ? 0 : e.quantity,
              max_purchase: e.max_purchase === undefined ? 1 : e.max_purchase,
              ...(isEditting && e.id && { id: e.id }),
            };
          });
        return {
          ...(isEditting && {
            id: currentFlashsaleStocks[0].collection_item_id,
          }),
          product_id,
          flash_sale_discounts_attributes,
          flash_sale_stocks_attributes,
          order_level: index,
        };
      });

      setLoadingButton(true);
      const hasErrors = values.collection_items_attributes.some((item) => {
        const emptyDiscount = item.flash_sale_discounts_attributes.some(
          (e) => e.fixed_price === null && e.deduction_percentage === null
        );

        const emptyStock =
          item.flash_sale_stocks_attributes.some(
            (e) => e.quantity === null || e.max_purchase === null
          ) || !item.flash_sale_stocks_attributes.length;

        return emptyDiscount || emptyStock;
      });

      if (hasErrors) {
        return message.error(
          'Please fill out all required fields on flashsale product'
        );
      }

      if (id) {
        const deletedProduct = [];
        Object.keys(mapListDeletedProduct).forEach((product_id) => {
          if (mapListDeletedProduct[product_id]._destroy) {
            deletedProduct.push(mapListDeletedProduct[product_id]);
          }
        });
        values[FIELDS.PRODUCT_LIST].push(...deletedProduct);
        values.weight = Number(values.weight) * 1000;
        await FlashsaleApi.updateFlashsale(id, { ...valueCopy, ...values });
        message.success('Successfully updated flashsale');
      } else {
        await FlashsaleApi.createFlashsale({ ...valueCopy, ...values });
        message.success('Successfully created flashsale');
      }
      navigate({
        pathname: '/flash_sales',
      });
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );

      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingButton(false);
    }
  }

  function changeValue(key, e) {
    if (e) {
      setValue((prevState) => ({
        ...prevState,
        [key]: e,
      }));
    }
  }

  function changePage(page) {
    setPage(page);
    fetchAllProduct(page, searchValue, true);
  }

  function onChecked(i, e) {
    const newProductList = allProducts.slice(0);
    newProductList[i].checked = e.target.checked;
    setAllProducts(newProductList);
  }

  function submitModal() {
    const newProduct = allProducts
      .slice(0)
      .filter(
        (product) =>
          product.checked &&
          productList.filter((e) => e.id === product.id).length === 0
      )
      .map((product) => {
        product.flash_sale_discounts = [
          {
            product_collection_id: +id,
            fixed_price: null,
            deduction_percentage: null,
          },
        ];
        product.flash_sale_stocks = [
          {
            warehouse_id: +warehouseList.find((w) => w.primary).id,
            product_collection_id: +id,
          },
        ];

        return product;
      });

    const alllist = [...productList, ...newProduct].filter(
      (prod, i, arr) => arr.findIndex((t) => t.id === prod.id) === i
    );
    const newProducts = alllist.map((item, i) => {
      item.order_level = i;
      return item;
    });

    setProductList(newProducts);
    exitModal();
  }

  function searchOnClick() {
    fetchAllProduct(1, searchValue);
  }

  function exitModal() {
    setProductNextPage(1);
    setSearchValue('');
    setPage(1);
    setAllProducts([]);
    setModalVisible(false);
  }

  function changeSlug(e) {
    setFlashsaleSlug(e.target.value);
  }

  function changeSearchValue(e) {
    setSearchValue(e.target.value);
  }

  function handleChangeValue(url, key) {
    setValue((prevState) => ({
      ...prevState,
      [key]: url,
    }));
  }

  useEffect(() => {
    if (id) {
      getFlashsale();
    } else {
      setValue([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, id]);

  useEffect(() => {
    const getWarehouseList = async () => {
      try {
        const {
          data: { data },
        } = await WarehouseApi.getWarehouseList({
          status: 'active',
        });
        setWarehouseList(data);
      } catch (error) {
        errHandling(error);
      }
    };

    getWarehouseList();
  }, []);

  useEffect(() => {
    if (id && value.title) {
      form.setFieldsValue({
        [FIELDS.TITLE]: value[FIELDS.TITLE],
        [FIELDS.START_DATETIME]: value[FIELDS.START_DATETIME],
        [FIELDS.END_DATETIME]: value[FIELDS.END_DATETIME],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, id]);

  if (loadingPage) {
    return <PageSpinner />;
  }

  return (
    <Layout className="pd-cms-flashsale main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">
            {id ? 'Edit' : 'Create New'} Flashsale
          </div>
          <Content className="pageContent">
            <Form form={form} layout={formLayout} onFinish={handleSubmit}>
              <div className="section-container">
                <div className="inner-section">
                  <Form.Item
                    label="Title"
                    {...formItemLayout}
                    name={FIELDS.TITLE}
                    rules={[
                      { message: 'Please input Title!', required: true },
                    ]}>
                    <Input
                      placeholder="Write flashsale title here"
                      onChange={(e) =>
                        changeValue(FIELDS.TITLE, e.target.value)
                      }
                      value={value[FIELDS.TITLE]}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Slug (optional)"
                    {...formItemLayout}
                    name="not-slug"
                    normalize={(value) => {
                      const arr = value.split(' ');
                      if (arr[arr.length - 1] === '') {
                        return value;
                      } else {
                        return slugify(value, {
                          remove: /[*+~.()'"`/><!:;?@{}=+]/g,
                        });
                      }
                    }}>
                    <Input
                      placeholder="Write flashsale slug here"
                      onChange={changeSlug}
                      value={flashsaleSlug}
                    />
                    Flashsale URL: https://www.homestory.co.id/flash-sale/
                    {slugify(flashsaleSlug, {
                      remove: /[*+~.()'"`/><!:;?@{}=+]/g,
                    })}
                  </Form.Item>
                  <Row gutter={20}>
                    <Col span={12}>
                      <Form.Item
                        label="Start Date"
                        {...formItemLayout}
                        name={FIELDS.START_DATETIME}
                        rules={[
                          { message: 'Please input date!', required: true },
                        ]}>
                        <Datetime
                          inputProps={{
                            className: 'ant-input',
                          }}
                          timeFormat="HH:mm"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="End Date"
                        {...formItemLayout}
                        name={FIELDS.END_DATETIME}
                        rules={[
                          { message: 'Please input date!', required: true },
                        ]}>
                        <Datetime
                          inputProps={{
                            className: 'ant-input',
                          }}
                          timeFormat="HH:mm"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Desktop Banner (1200x200 px)"
                    {...formItemLayout}>
                    <PdUploader
                      afterUpload={handleChangeValue}
                      imgClassName="w-90"
                      imgUrl={value[FIELDS.BANNER_IMAGE]}
                      keyValue={FIELDS.BANNER_IMAGE}
                      name="Desktop Banner (1200x200 px)"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Mobile Banner (640x320 px)"
                    {...formItemLayout}>
                    <PdUploader
                      afterUpload={handleChangeValue}
                      imgClassName="w-half"
                      imgUrl={value[FIELDS.MOBILE_MEDIA_URL]}
                      keyValue={FIELDS.MOBILE_MEDIA_URL}
                      name="Mobile Image (640x320 px)"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="section-container">
                <div className="inner-section">
                  <div>
                    <Row gutter={20}>
                      <Col span={20}>
                        <Title className="pageSubtitle" level={3}>
                          Products
                        </Title>
                      </Col>
                      <Col span={4}>
                        <PdButton full onClick={showModal} type="primary">
                          Add Product
                        </PdButton>
                      </Col>
                      <Col className="pd-cms-flashsale--product-list" span={24}>
                        <EditableTable
                          dataSource={productList}
                          setDataSource={setProductList}
                          warehouseList={warehouseList}
                          flash_sale_id={id}
                          setMapListDeletedProduct={setMapListDeletedProduct}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
                <Form.Item {...buttonItemLayout}>
                  <div className="buttonSection mt-40">
                    <PdButton
                      disabled={hasErrors()}
                      full
                      htmlType="submit"
                      loading={loadingButton}
                      type="primary">
                      Publish
                    </PdButton>
                  </div>
                </Form.Item>
              </div>
            </Form>
          </Content>
        </div>
      </div>
      <Modal
        centered
        className="modal--productList"
        onCancel={exitModal}
        onOk={exitModal}
        open={modalVisible}
        wrapClassName="hidden-wrap-modal">
        {loadModal ? (
          <div className="modal-container">
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : (
          <div className="modal-container">
            <Row>
              <Col className="header" span={24}>
                <h1 className="bold">Add Product</h1>
              </Col>
              <Col span={24}>
                <Search
                  className="search-product"
                  enterButton
                  onChange={changeSearchValue}
                  onSearch={searchOnClick}
                  placeholder="Search Product List"
                  value={searchValue}
                />
              </Col>
              <Col className="product-catalog" span={24}>
                {allProducts.map((product, i) => (
                  <Row className="product-item" key={i}>
                    <Col span={1}>
                      <Checkbox
                        checked={product.checked}
                        onChange={onChecked.bind(this, i)}
                      />
                    </Col>
                    <Col className="product-title" span={23}>
                      <span className=" bold">{product.title}</span>
                    </Col>
                  </Row>
                ))}
                <Row>
                  <Col offset={8} span={8}>
                    <div className="center-button">
                      {productNextPage !== -1 && (
                        <PdButton
                          className="custom-button"
                          onClick={changePage.bind(this, Number(page) + 1)}
                          outline
                          type="primary">
                          Load More
                        </PdButton>
                      )}
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col className="button-container" offset={7} span={10}>
                <PdButton full onClick={submitModal} type="primary">
                  Add Product
                </PdButton>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </Layout>
  );
}

export default FlashsaleCreateEdit;
